import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Building2, Plus, Search, Users, FileText, Power, PowerOff, Pencil, ShieldOff, ShieldCheck, StickyNote, Pin, Trash2, X, UserCircle2, History } from "lucide-react";
import { toast } from "sonner";
import UserManagementPanel from "@/components/UserManagementPanel";
import { fmtDate, fmtDateTime } from "@/lib/dateFormat";

export default function AdminClientsPage() {
  const [clients, setClients] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [active, setActive] = useState(null);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/admin/clients");
      setClients(data);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const filtered = clients.filter((c) =>
    !search || c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AppShell>
      <div className="mb-8">
        <div className="font-overline text-[#4A5568] mb-2">Staff console</div>
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#0A192F]" data-testid="admin-clients-title">Client Companies</h1>
            <p className="text-[#4A5568] mt-2 max-w-2xl">All property-management companies on the platform. Create new clients, suspend or rename them, and audit their team.</p>
          </div>
          <CreateClientDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
        </div>
      </div>

      <div className="bg-white border border-[#0A192F]/10 rounded-sm">
        <div className="px-5 py-4 border-b border-[#0A192F]/10 flex items-center gap-3">
          <Search className="h-4 w-4 text-[#4A5568]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search companies…" className="border-0 shadow-none focus-visible:ring-0 h-9"
            data-testid="admin-clients-search" />
          <span className="text-xs text-[#4A5568] font-mono">{filtered.length} client{filtered.length !== 1 ? "s" : ""}</span>
        </div>
        {error && <div className="p-4 text-[#EF4444] text-sm">{error}</div>}
        <ul className="divide-y divide-[#0A192F]/8">
          {filtered.map((c) => (
            <ClientRow key={c.id} c={c} onOpen={() => setActive(c)} />
          ))}
        </ul>
      </div>

      <ClientDetailDialog client={active} onClose={() => setActive(null)} onChanged={load} />
    </AppShell>
  );
}

function ClientRow({ c, onOpen }) {
  const suspended = !!c.suspended_at;
  const navigate = useNavigate();
  return (
    <li className="px-5 py-4 grid grid-cols-1 sm:grid-cols-12 gap-3 items-center cursor-pointer hover:bg-[#F3EFEA]/40 transition-colors"
        onClick={() => navigate(`/admin/clients/${c.id}`)} data-testid={`admin-client-${c.id}`}>
      <div className="sm:col-span-5 flex items-center gap-3 min-w-0">
        <div className="h-10 w-10 rounded-sm bg-[#F3EFEA] flex items-center justify-center shrink-0">
          <Building2 className="h-5 w-5 text-[#0A192F]" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <div className="font-serif text-base text-[#0A192F] truncate">{c.name}</div>
          <div className="text-[11px] text-[#4A5568] font-mono truncate">{c.id.slice(0, 8).toUpperCase()} · {fmtDate(c.created_at)}</div>
        </div>
      </div>
      <div className="sm:col-span-3 flex items-center gap-2 text-xs text-[#4A5568]">
        {c.account_number ? (
          <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-[#0A192F]/20 text-[#0A192F] bg-[#F3EFEA]"
                 data-testid={`admin-client-account-${c.id}`}>
            #{c.account_number}
          </Badge>
        ) : (
          <span className="text-[10px] font-mono text-[#4A5568]/60 uppercase tracking-[0.14em]">No account #</span>
        )}
        <span className="mx-1">·</span>
        <Users className="h-3.5 w-3.5" />{c.users_count}
        <span className="mx-1">·</span>
        <FileText className="h-3.5 w-3.5" />{c.screenings_count}
      </div>
      <div className="sm:col-span-2 text-xs text-[#4A5568]">
        Fee cap {c.screening_fee_cap_usd ? `$${Number(c.screening_fee_cap_usd).toFixed(2)}` : "default"}
      </div>
      <div className="sm:col-span-2 flex items-center gap-2 justify-end">
        {c.assigned_ntn_rep_name && (
          <span className="hidden lg:inline-flex items-center gap-1 text-[10px] font-mono text-slate-600 truncate max-w-[120px]" title={`Rep: ${c.assigned_ntn_rep_name}`}>
            <UserCircle2 className="h-3 w-3" /> {c.assigned_ntn_rep_name.split(" ")[0]}
          </span>
        )}
        {suspended
          ? <Badge variant="outline" className="rounded-sm text-[10px] border-[#EF4444]/40 text-[#B91C1C]"><PowerOff className="h-3 w-3 mr-1" /> SUSPENDED</Badge>
          : <Badge variant="outline" className="rounded-sm text-[10px] border-[#22C55E]/40 text-[#15803D]"><Power className="h-3 w-3 mr-1" /> ACTIVE</Badge>}
      </div>
    </li>
  );
}

function CreateClientDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ company_name: "", admin_email: "", admin_name: "", admin_password: "", screening_fee_cap_usd: "", account_number: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      payload.screening_fee_cap_usd = form.screening_fee_cap_usd ? parseFloat(form.screening_fee_cap_usd) : null;
      payload.account_number = form.account_number.trim() || null;
      await apiClient.post("/admin/clients", payload);
      toast.success(`Client ${form.company_name} created`);
      setForm({ company_name: "", admin_email: "", admin_name: "", admin_password: "", screening_fee_cap_usd: "", account_number: "" });
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-11 px-5" data-testid="admin-create-client-btn">
          <Plus className="h-4 w-4 mr-2" /> New Client
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Onboard a new client</DialogTitle>
          <DialogDescription>Creates the company and its first admin user. Share the temp password privately.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Company name">
            <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="h-11 rounded-sm" data-testid="admin-client-company" />
          </Field>
          <Field label="Admin name">
            <Input value={form.admin_name} onChange={(e) => set("admin_name", e.target.value)} className="h-11 rounded-sm" data-testid="admin-client-admin-name" />
          </Field>
          <Field label="Admin email">
            <Input type="email" value={form.admin_email} onChange={(e) => set("admin_email", e.target.value)} className="h-11 rounded-sm" data-testid="admin-client-admin-email" />
          </Field>
          <Field label="Temporary password">
            <Input value={form.admin_password} onChange={(e) => set("admin_password", e.target.value)}
              placeholder="≥10 chars · upper · lower · digit · symbol"
              className="h-11 rounded-sm font-mono" data-testid="admin-client-admin-password" />
          </Field>
          <Field label="Screening-fee cap (optional, USD)">
            <Input type="number" step="0.01" value={form.screening_fee_cap_usd}
              onChange={(e) => set("screening_fee_cap_usd", e.target.value)}
              placeholder="62.02" className="h-11 rounded-sm" data-testid="admin-client-fee-cap" />
          </Field>
          <Field label="Account number (optional)">
            <Input value={form.account_number} onChange={(e) => set("account_number", e.target.value)}
              placeholder="e.g. NTN-4421 or your internal code"
              className="h-11 rounded-sm font-mono" data-testid="admin-client-account-number" />
          </Field>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !form.company_name || !form.admin_email || !form.admin_name || !form.admin_password}
            className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm" data-testid="admin-client-submit">
            {busy ? "Creating…" : "Create client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClientDetailDialog({ client, onClose, onChanged }) {
  const [users, setUsers] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [editName, setEditName] = useState("");
  const [editFee, setEditFee] = useState("");
  const [editAccountNumber, setEditAccountNumber] = useState("");
  const [editAppLink, setEditAppLink] = useState("");
  const [editAssignedRep, setEditAssignedRep] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!client) return;
    setEditName(client.name);
    setEditFee(client.screening_fee_cap_usd != null ? String(client.screening_fee_cap_usd) : "");
    setEditAccountNumber(client.account_number || "");
    setEditAppLink(client.application_link_url || "");
    setEditAssignedRep(client.assigned_ntn_rep_id || "");
    apiClient.get(`/admin/clients/${client.id}/users`)
      .then(({ data }) => setUsers(Array.isArray(data) ? data : (data.users || [])))
      .catch(() => setUsers([]));
    apiClient.get(`/admin/staff`)
      .then(({ data }) => setStaffOptions(data || []))
      .catch(() => setStaffOptions([]));
  }, [client]);

  if (!client) return null;
  const suspended = !!client.suspended_at;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (editName !== client.name) payload.company_name = editName;
      if (editFee !== (client.screening_fee_cap_usd != null ? String(client.screening_fee_cap_usd) : ""))
        payload.screening_fee_cap_usd = editFee ? parseFloat(editFee) : null;
      if (editAccountNumber.trim() !== (client.account_number || ""))
        payload.account_number = editAccountNumber.trim();
      if (editAppLink.trim() !== (client.application_link_url || ""))
        payload.application_link_url = editAppLink.trim();
      if (editAssignedRep !== (client.assigned_ntn_rep_id || ""))
        payload.assigned_ntn_rep_id = editAssignedRep || "";
      if (Object.keys(payload).length) {
        await apiClient.patch(`/admin/clients/${client.id}`, payload);
        toast.success("Client updated");
        onChanged();
      }
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleSuspend = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/admin/clients/${client.id}/status`, { status: suspended ? "active" : "suspended" });
      toast.success(suspended ? "Client unsuspended" : "Client suspended — all sessions revoked");
      onChanged();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="admin-client-detail">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Building2 className="h-5 w-5" /> {client.name}
          </DialogTitle>
          <DialogDescription>
            {client.users_count} user{client.users_count !== 1 ? "s" : ""} · {client.screenings_count} screening{client.screenings_count !== 1 ? "s" : ""} · created {fmtDate(client.created_at)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Company name">
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-11 rounded-sm" data-testid="admin-client-edit-name" />
            </Field>
            <Field label="Screening-fee cap (USD)">
              <Input type="number" step="0.01" value={editFee} onChange={(e) => setEditFee(e.target.value)}
                placeholder="default" className="h-11 rounded-sm" data-testid="admin-client-edit-fee" />
            </Field>
            <Field label="Account number">
              <Input value={editAccountNumber} onChange={(e) => setEditAccountNumber(e.target.value)}
                placeholder="e.g. NTN-4421" className="h-11 rounded-sm font-mono"
                data-testid="admin-client-edit-account-number" />
            </Field>
            <Field label="Application link URL (sent in outreach emails)" className="sm:col-span-2">
              <Input type="url" value={editAppLink} onChange={(e) => setEditAppLink(e.target.value)}
                placeholder="https://applicant-portal.example.com/apply"
                className="h-11 rounded-sm font-mono text-sm"
                data-testid="admin-client-edit-application-link" />
              <p className="text-[11px] text-[#4A5568] mt-1.5">
                This URL is sent as the "Open application" button in outreach emails. Only NTN staff can change it.
              </p>
            </Field>
            <Field label="Assigned NTN representative" className="sm:col-span-2">
              <Select value={editAssignedRep || "__none__"}
                onValueChange={(v) => setEditAssignedRep(v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-11 rounded-sm" data-testid="admin-client-edit-assigned-rep">
                  <SelectValue placeholder="— Unassigned —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Unassigned —</SelectItem>
                  {staffOptions.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} · {s.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <UserManagementPanel clientId={client.id} clientName={client.name} onChanged={onChanged} />

          <NotesPanel clientId={client.id} />
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className={`rounded-sm border-[#EF4444]/30 text-[#B91C1C] hover:bg-[#EF4444]/10`}
                      data-testid="admin-client-toggle-suspend">
                {suspended ? <><ShieldCheck className="h-4 w-4 mr-2" /> Unsuspend</> : <><ShieldOff className="h-4 w-4 mr-2" /> Suspend</>}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{suspended ? `Unsuspend ${client.name}?` : `Suspend ${client.name}?`}</AlertDialogTitle>
                <AlertDialogDescription>
                  {suspended
                    ? "Their users can log in again. Existing screenings remain unchanged."
                    : "All users will be logged out and blocked from signing in. Their data is preserved. You can unsuspend at any time."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={toggleSuspend} className={suspended ? "bg-[#0A192F]" : "bg-[#EF4444]"} data-testid="admin-client-toggle-suspend-confirm">
                  {suspended ? "Unsuspend" : "Suspend"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button onClick={save} disabled={busy} className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm" data-testid="admin-client-save">
            <Pencil className="h-4 w-4 mr-2" /> Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

/* ---------------- Notes panel (NTN-staff only timeline per subscriber) ---------------- */
function NotesPanel({ clientId }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/admin/clients/${clientId}/notes`);
      setNotes(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, [clientId]); // eslint-disable-line

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await apiClient.post(`/admin/clients/${clientId}/notes`, { text: text.trim(), pinned });
      setText(""); setPinned(false);
      toast.success("Note added");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add note");
    } finally { setBusy(false); }
  };

  const togglePin = async (n) => {
    try {
      await apiClient.patch(`/admin/clients/${clientId}/notes/${n.id}`, {
        text: n.text, pinned: !n.pinned,
      });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };

  const remove = async (n) => {
    try {
      await apiClient.delete(`/admin/clients/${clientId}/notes/${n.id}`);
      toast.success("Note removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div data-testid="client-notes-panel">
      <div className="flex items-center gap-2 mb-2">
        <StickyNote className="h-4 w-4 text-[#0A192F]" />
        <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">
          Internal notes ({notes.length})
        </Label>
        <span className="text-[9px] font-mono tracking-[0.18em] text-[#4A5568] uppercase ml-auto">
          NTN staff only
        </span>
      </div>

      {/* Composer */}
      <div className="border border-[#0A192F]/10 rounded-sm p-3 bg-[#F3EFEA]/40">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Add a note about this subscriber — e.g., contract renewal, billing context, calls…"
          className="rounded-sm bg-white" data-testid="client-note-text" />
        <div className="flex items-center justify-between mt-2 gap-2">
          <label className="flex items-center gap-2 text-xs text-[#0A192F] cursor-pointer">
            <Checkbox checked={pinned} onCheckedChange={(v) => setPinned(!!v)} data-testid="client-note-pinned" />
            <Pin className="h-3 w-3" /> Pin to top
          </label>
          <Button onClick={submit} disabled={busy || !text.trim()} size="sm"
            className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-9 px-4" data-testid="client-note-add">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add note
          </Button>
        </div>
      </div>

      {error && <div className="text-[#EF4444] text-xs mt-2">{error}</div>}

      {/* Notes list */}
      <ul className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-1">
        {notes.length === 0 ? (
          <li className="text-xs text-[#4A5568] text-center py-6 italic">No notes yet. First note above.</li>
        ) : notes.map((n) => (
          <NoteRow key={n.id} n={n} self={user} onPin={() => togglePin(n)} onRemove={() => remove(n)} />
        ))}
      </ul>
    </div>
  );
}

function NoteRow({ n, self, onPin, onRemove }) {
  const canEdit = n.author_id === self?.id || self?.account_role === "admin";
  return (
    <li className={`border rounded-sm p-3 text-sm ${n.pinned ? "border-[#F59E0B]/40 bg-[#F59E0B]/5" : "border-[#0A192F]/10 bg-white"}`}
        data-testid={`client-note-${n.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          {n.pinned && (
            <div className="text-[10px] font-mono tracking-[0.14em] text-[#92400E] uppercase flex items-center gap-1 mb-1">
              <Pin className="h-3 w-3" /> PINNED
            </div>
          )}
          <div className="text-[#0A192F] whitespace-pre-wrap break-words">{n.text}</div>
          <div className="text-[10px] font-mono tracking-[0.1em] text-[#4A5568] uppercase mt-2 flex flex-wrap gap-2">
            <span>{n.author_name}</span>
            <span>·</span>
            <span>{fmtDateTime(n.created_at)}</span>
            {n.edited_at && <><span>·</span><span>edited</span></>}
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-col gap-1 shrink-0">
            <button type="button" onClick={onPin}
              title={n.pinned ? "Unpin" : "Pin"}
              data-testid={`client-note-toggle-pin-${n.id}`}
              className={`h-7 w-7 rounded-sm flex items-center justify-center ${n.pinned ? "text-[#92400E] hover:bg-[#F59E0B]/15" : "text-[#4A5568] hover:bg-[#0A192F]/5"}`}>
              {n.pinned ? <X className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button type="button"
                  title="Delete note"
                  data-testid={`client-note-remove-${n.id}`}
                  className="h-7 w-7 rounded-sm flex items-center justify-center text-[#EF4444] hover:bg-[#EF4444]/10">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this note?</AlertDialogTitle>
                  <AlertDialogDescription>
                    It will be removed from the timeline. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onRemove} className="bg-[#EF4444]" data-testid={`client-note-remove-confirm-${n.id}`}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
    </li>
  );
}

